"use client";

import { Box, Flex, keyframes } from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { TenantLogo } from "@/components/shared/TenantLogo";
import { Text } from "@/components/ui/Text";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useInteractiveTokens } from "@/lib/theme/tokens/interactive";
import type { GetTenantResponse } from "@/openapi/generated";

const OVERLAY_Z_INDEX = 1500;

const ringSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const logoPulse = keyframes`
  0%, 100% { opacity: 0.88; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.03); }
`;

type LogoutTransitionOverlayProps = {
  isVisible: boolean;
  logoUrl?: string | null;
  tenantData?: GetTenantResponse | null;
};

export const LogoutTransitionOverlay = ({
  isVisible,
  logoUrl,
  tenantData,
}: LogoutTransitionOverlayProps) => {
  const theme = useThemeTokens();
  const interactive = useInteractiveTokens();
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  if (!portalTarget) return null;

  return createPortal(
    <AnimatePresence>
      {isVisible ? (
        <motion.div
          key="logout-transition-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: OVERLAY_Z_INDEX,
          }}
          data-testid="logout-transition-overlay"
        >
          <Flex
            position="absolute"
            inset={0}
            align="center"
            justify="center"
            bg={
              theme.mode === "dark"
                ? "rgba(11,13,16,0.78)"
                : "rgba(255,255,255,0.78)"
            }
            backdropFilter={interactive.backdrop.blurStrong}
            sx={{ WebkitBackdropFilter: interactive.backdrop.blurStrong }}
          >
            <Box
              position="absolute"
              inset={0}
              pointerEvents="none"
              bg={
                theme.mode === "dark"
                  ? "radial-gradient(ellipse at center, rgba(255,255,255,0.07) 0%, transparent 68%)"
                  : "radial-gradient(ellipse at center, rgba(11,13,16,0.05) 0%, transparent 68%)"
              }
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.94, filter: "blur(10px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              transition={{
                duration: 0.45,
                ease: [0.4, 0, 0.2, 1],
                delay: 0.06,
              }}
            >
              <Flex direction="column" align="center" gap={5}>
                <Box position="relative" w="96px" h="96px">
                  <Box
                    position="absolute"
                    inset="-10px"
                    borderRadius="full"
                    borderWidth="2px"
                    borderStyle="solid"
                    borderColor="transparent"
                    borderTopColor={theme.accent.primary}
                    borderRightColor={`${theme.accent.primary}55`}
                    animation={`${ringSpin} 1.15s linear infinite`}
                  />
                  <Flex
                    position="absolute"
                    inset={0}
                    align="center"
                    justify="center"
                    animation={`${logoPulse} 2.2s ease-in-out infinite`}
                  >
                    <TenantLogo
                      width={60}
                      height={60}
                      tenantData={tenantData}
                      src={logoUrl}
                      priority
                    />
                  </Flex>
                </Box>

                <Text
                  fontSize="sm"
                  fontWeight="500"
                  color={theme.text.secondary}
                  letterSpacing="0.02em"
                >
                  Signing you out...
                </Text>
              </Flex>
            </motion.div>
          </Flex>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    portalTarget,
  );
};
